import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { environment } from '../../../../environments/environment';
import { CrmClientCommunication } from '../../../crm/crm.model';
import { SessionStorageService } from '../../../services/session-storage.service';

@Component({
  selector: 'app-report-email',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, SelectModule, TextareaModule],
  template: `
    <p-button label="Email" icon="pi pi-envelope" severity="secondary" [outlined]="true"
      [disabled]="!rows.length" (onClick)="open()"></p-button>
    <p-dialog [(visible)]="visible" header="Email Report" [modal]="true" [style]="{width:'32rem'}" appendTo="body">
      <div class="email-form">
        <label>CRM Contact</label>
        <p-select [options]="contacts" [(ngModel)]="contactId" optionValue="id" [optionLabel]="'displayLabel'"
          [filter]="true" filterBy="displayLabel,email" appendTo="body" placeholder="Select contact"></p-select>
        <label>Subject</label>
        <input pInputText [(ngModel)]="subject" />
        <label>Message</label>
        <textarea pTextarea [(ngModel)]="message" rows="5"></textarea>
      </div>
      <ng-template pTemplate="footer">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="visible=false"></p-button>
        <p-button label="Send" icon="pi pi-send" [loading]="sending" [disabled]="!contactId" (onClick)="send()"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`.email-form{display:grid;gap:.65rem}.email-form label{font-size:.8rem;font-weight:600;color:#475569}.email-form p-select,.email-form input,.email-form textarea{width:100%}`]
})
export class ReportEmailComponent {
  @Input({ required: true }) reportName = 'Accounting Report';
  @Input() rows: Record<string, unknown>[] | unknown[] = [];
  visible = false;
  sending = false;
  contacts: (CrmClientCommunication & { displayLabel: string })[] = [];
  contactId?: number;
  subject = '';
  message = 'Please find the requested accounting report attached.';

  constructor(
    private http: HttpClient,
    private session: SessionStorageService,
    private messages: MessageService
  ) {}

  open(): void {
    this.subject = this.reportName;
    this.visible = true;
    const companyId = this.session.getCompany()?.id;
    if (!companyId) return;
    this.http.get<CrmClientCommunication[]>(`${environment.restApiUrl}accounting/reports/v2/email/contacts/${companyId}`)
      .subscribe({
        next: contacts => this.contacts = (contacts ?? []).map(contact => ({
          ...contact,
          displayLabel: `${contact.name || 'CRM Contact'}${contact.email ? ` - ${contact.email}` : ''}`
        })),
        error: () => this.messages.add({ severity: 'error', summary: 'Error', detail: 'Unable to load CRM contacts.' })
      });
  }

  send(): void {
    const companyId = this.session.getCompany()?.id;
    if (!companyId || !this.contactId) return;
    this.sending = true;
    this.http.post(`${environment.restApiUrl}accounting/reports/v2/email`, {
      owningCompanyId: companyId,
      crmContactId: this.contactId,
      reportName: this.reportName,
      subject: this.subject,
      message: this.message,
      csvContent: this.toCsv(this.rows)
    }).subscribe({
      next: () => {
        this.sending = false;
        this.visible = false;
        this.messages.add({ severity: 'success', summary: 'Sent', detail: 'Report emailed to the selected CRM contact.' });
      },
      error: () => {
        this.sending = false;
        this.messages.add({ severity: 'error', summary: 'Error', detail: 'Unable to email report.' });
      }
    });
  }

  private toCsv(source: unknown[]): string {
    const rows = source.map(value => (value && typeof value === 'object' ? value as Record<string, unknown> : { value }));
    const columns = [...new Set(rows.flatMap(row => Object.keys(row)))]
      .filter(column => !/(?:^id$|Id$|ID$|_id$)/.test(column) && column !== 'partnerType');
    const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    return [columns.map(escape).join(','), ...rows.map(row => columns.map(column => {
      const value = row[column];
      return escape(typeof value === 'object' ? JSON.stringify(value) : value);
    }).join(','))].join('\r\n');
  }
}
